import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@getro/rombo';
import { Text, Flex, Box } from 'rebass/styled-components';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import { Circle } from 'lucide-react';
import { BoostIcon } from './BoostIcon';
import { getJobBoostStatus } from '../jobBoostStatus';

const useJobBoostStatus = (currentJob, onLaunchApplicationBoostAgent) =>
  useMemo(
    () => getJobBoostStatus(currentJob, onLaunchApplicationBoostAgent),
    [currentJob, onLaunchApplicationBoostAgent],
  );

export const ApplicationBoostCell = ({ job, onLaunchApplicationBoostAgent }) => {
  const jobBoostStatus = useJobBoostStatus(job, onLaunchApplicationBoostAgent);

  const shouldExcludeForOurAgents = ['executive', 'cxo', 'vice_president'].includes(job.seniority);
  const isJobNotVisibleOrHidden = ['hidden', 'not_visible', 'hidden_forced'].includes(job.visibility);

  return (
    <Flex alignItems="center" sx={{ gap: '8px' }} color="text.main">
      {isJobNotVisibleOrHidden && (
        <Box
          sx={{
            color: 'text.subtle',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '24px',
            fontStyle: 'italic',
          }}
        >
          Not available for non-visible jobs
        </Box>
      )}
      {!isJobNotVisibleOrHidden && shouldExcludeForOurAgents && (
        <Tooltip
          content="AI boosting works best for roles up to Director level. Executive roles require a more tailored approach."
          position="top-start"
          trigger={
            <Box
              sx={{
                color: 'text.subtle',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '24px',
                fontStyle: 'italic',
              }}
            >
              Not eligible
            </Box>
          }
        />
      )}
      {!isJobNotVisibleOrHidden && !shouldExcludeForOurAgents && (
        <DynamicTooltip
          useTooltip={Boolean(jobBoostStatus.tooltip)}
          content={jobBoostStatus.tooltip}
          position="top-start"
          sx={{
            '.tooltip-arrow': {
              left: '-50px !important',
            },
          }}
        >
          <Flex alignItems="center" sx={{ gap: '8px' }}>
            {jobBoostStatus.id === 'not_boosted' && (
              <Flex
                alignItems="center"
                sx={{ gap: '4px', cursor: 'pointer', textDecoration: 'underline', color: 'text.dark' }}
                onClick={() => {
                  if (jobBoostStatus?.action) {
                    jobBoostStatus?.action(job);
                  }
                }}
              >
                Boost job
                <Box
                  width="16px"
                  height="16px"
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <BoostIcon />
                </Box>
              </Flex>
            )}
            {jobBoostStatus.id === 'in_progress' && (
              <>
                <Box sx={jobBoostStatus.sx}>
                  <Text
                    as="span"
                    fontSize="14px"
                    color="text.dark"
                    sx={{
                      fontWeight: '500',
                      lineHeight: '24px',
                    }}
                  >
                    {jobBoostStatus.label}
                  </Text>
                  <BoostIcon />
                </Box>
                <Box
                  alignItems="center"
                  sx={{
                    opacity: 0,
                    position: 'relative',
                    top: '0',
                    left: '-24px',
                    transition: 'all 0.3s ease-in-out',
                    '.ag-row-hover &': {
                      opacity: 1,
                      top: '0',
                      left: 0,
                    },
                  }}
                >
                  <Text
                    sx={{
                      lineHeight: '24px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'text.dark',
                    }}
                    onClick={() => {
                      if (jobBoostStatus?.action) {
                        jobBoostStatus?.action(job);
                      }
                    }}
                    fontSize="14px"
                  >
                    {jobBoostStatus?.actionText}
                  </Text>
                </Box>
              </>
            )}
            {jobBoostStatus.id === 'calibrating' && (
              <Box
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '4px',
                  bg: jobBoostStatus.background || 'neutral.20',
                  px: '8px',
                  height: '24px',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                  <Box
                    as={Circle}
                    size={6}
                    sx={{
                      color: jobBoostStatus.color,
                      background: jobBoostStatus.color,
                      fill: jobBoostStatus.color,
                    }}
                  />
                </svg>
                <Text
                  as="span"
                  fontSize="14px"
                  color={jobBoostStatus.color || 'text.main'}
                  sx={{
                    fontWeight: '500',
                    lineHeight: '24px',
                  }}
                >
                  {jobBoostStatus.label}
                </Text>
              </Box>
            )}
            {!['not_boosted', 'in_progress', 'calibrating'].includes(jobBoostStatus.id) && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '4px',
                    bg: jobBoostStatus.background || 'neutral.20',
                    px: '8px',
                    height: '24px',
                    ...(jobBoostStatus.sx ? jobBoostStatus.sx : {}),
                  }}
                >
                  {jobBoostStatus.id !== 'finished' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                      <circle cx="3" cy="3" r="3" fill="#0F388A" />
                    </svg>
                  )}
                  <Text
                    as="span"
                    fontSize="14px"
                    color={jobBoostStatus.color || 'text.main'}
                    sx={{
                      fontWeight: '500',
                      lineHeight: '24px',
                    }}
                  >
                    {jobBoostStatus.label}
                  </Text>
                </Box>

                <Box
                  alignItems="center"
                  sx={{
                    opacity: 0,
                    position: 'relative',
                    left: '-24px',
                    transition: 'all 0.3s ease-in-out',
                    '.ag-row-hover &': {
                      opacity: 1,
                      left: 0,
                    },
                  }}
                >
                  <Text
                    sx={{
                      lineHeight: '24px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'text.dark',
                    }}
                    onClick={() => {
                      if (jobBoostStatus?.action) {
                        jobBoostStatus?.action(job);
                      }
                    }}
                    fontSize="14px"
                  >
                    {jobBoostStatus?.actionText}
                  </Text>
                </Box>
              </>
            )}
          </Flex>
        </DynamicTooltip>
      )}
    </Flex>
  );
};

ApplicationBoostCell.propTypes = {
  job: PropTypes.object.isRequired,
  onLaunchApplicationBoostAgent: PropTypes.func.isRequired,
};
